import { MerchantValidModel, RegisteringUserModel, ReimbursementDetailsModel } from "lib/carrot-api/Models"
import Helpers from "utils/Helpers"
import { fetchWrapper } from "lib/FetchWrapper/FetchWrapper"
import { v4 as uuid } from "uuid"
import { CarrotErrorCodes } from "utils/CarrotErrors"
import { reportError } from "utils/ErrorReporting"
import { HttpErrors } from "utils/HttpErrors"
import { UserInfoModel } from "lib/carrot-api/UserInfoModel"
import { CarrotPlan, CarrotPlanEmployeeSettings, CarrotPlanStepRequest } from "../../types/carrotPlanTypes"
import ResponseError from "../../types/responseError"
import { HttpStatusCodes } from "../../utils/HttpStatusCodes"
import { BenefitEnrollmentStatus } from "../../types/benefitEnrollmentStatus"

const CarrotClient = function (baseUrl) {
  this.baseUrl = baseUrl
}

async function processJsonResponse<T>(response, correlationId): Promise<T> {
  if (response.status === HttpStatusCodes.OK) {
    const responseText = await response.text()
    return JSON.parse(responseText)
  }
  throw new ResponseError(response, correlationId)
}

function buildRequestOptions(method: string): { options: RequestInit; correlationId: string } {
  const correlationId = uuid()
  const headers = new Headers()
  headers.append("X-Request-ID", correlationId)
  headers.append("Content-Type", "application/json")

  return {
    options: {
      credentials: "include",
      headers,
      method: method
    },
    correlationId
  }
}

CarrotClient.prototype = {
  apiV2PostJson: async function (path, payload) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify(payload)
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })

    try {
      return JSON.parse(await response.text())
    } catch {
      return null
    }
  },

  apiV2RequestWithRedirect: async function (request) {
    let response
    try {
      response = await request()
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        await Helpers.browserRedirect(`${window.location.pathname}?errorCode=${CarrotErrorCodes.NOT_AUTHENTICATED}`)
        return
      }
      throw error
    }
    return response
  },

  apiV2JsonWithRedirect: async function (path, method, payload = null) {
    return this.apiV2RequestWithRedirect(async () => {
      const headers = new Headers()
      headers.append("Content-Type", "application/json")
      const body = payload ? JSON.stringify(payload) : null

      return fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
        method,
        headers,
        body,
        credentials: "include"
      })
    })
  },

  apiV2GetJson: async function (path) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/${path}`, {
      method: "GET",
      headers,
      credentials: "include"
    })

    const responseText = await response.text()
    return JSON.parse(responseText)
  },

  signUp: async function (email) {
    return this.apiV2PostJson("sign-up", { email })
  },

  signUpFirstLastDateOfBirth: async function (email, firstName, lastName, dateOfBirth) {
    return this.apiV2PostJson("sign-up/find-account", { email, firstName, lastName, dateOfBirth })
  },

  signUpAddEmailToAccount: async function (email, firstName, lastName, dateOfBirth) {
    return this.apiV2PostJson("sign-up/email", { email, firstName, lastName, dateOfBirth })
  },

  logout: async function () {
    return this.apiV2PostJson("auth/logout", {})
  },

  sendTalkToCarrotMessage: async function (message, subject, sendFromPartner, provider, topic) {
    return this.apiV2PostJson("talk-to-carrot/send-talk-to-carrot-message", {
      message,
      subject,
      sendFromPartner,
      provider,
      topic
    })
  },

  sendTalkToCarrotSignUpMessage: async function (message, topic, sendFromPartner, provider, email, name) {
    return this.apiV2PostJson("talk-to-carrot-signup/get-help", {
      message,
      topic,
      sendFromPartner,
      provider,
      email,
      name
    })
  },

  lookup: async function (email) {
    return await this.apiV2PostJson("users/lookup", { email })
  },

  login: async function (email, password) {
    return await this.apiV2PostJson("auth/login", { email, password })
  },

  validateResetPasswordGuid: async function (passwordResetGuid) {
    return this.apiV2PostJson("auth/validate-reset-guid", {
      passwordResetGuid
    })
  },

  resetPassword: async function (password, confirmPassword, passwordResetGuid) {
    return this.apiV2PostJson("auth/reset-password", {
      password,
      confirmPassword,
      passwordResetGuid
    })
  },

  submitCardExpense: async function (
    fileGuids,
    whoReceivedCare,
    isInfertility,
    isMedicalNecessity,
    memberNotes,
    expenseId,
    expenseJourneyType
  ) {
    return this.apiV2JsonWithRedirect("statements/submitCardExpense", "POST", {
      fileGuids,
      whoReceivedCare,
      isInfertility,
      isMedicalNecessity,
      memberNotes,
      expenseId,
      expenseJourneyType
    })
  },

  submitExpense: async function (
    fileGuids,
    whoReceivedCare,
    isInfertility,
    isMedicalNecessity,
    memberNotes,
    expenseJourneyType
  ) {
    return this.apiV2JsonWithRedirect("statements/submitExpense", "POST", {
      fileGuids,
      whoReceivedCare,
      isInfertility,
      isMedicalNecessity,
      memberNotes,
      expenseJourneyType
    })
  },

  startPhoneVerification: async function (phoneNumber, countryCode) {
    return this.apiV2JsonWithRedirect("phoneVerification/start", "POST", {
      phoneNumber,
      countryCode
    })
  },

  completePhoneVerification: async function (phoneNumber, code, countryCode) {
    return this.apiV2JsonWithRedirect("phoneVerification/complete", "POST", {
      phoneNumber,
      code,
      countryCode
    })
  },

  completePhoneVerificationAndRequestCard: async function (phoneNumber, code, countryCode) {
    return this.apiV2JsonWithRedirect("card/verification/complete", "POST", {
      phoneNumber,
      code,
      countryCode
    })
  },

  createCardholder: async function (address1, address2, city, state, zip) {
    return this.apiV2JsonWithRedirect("card/createcardholder", "POST", {
      address1,
      address2,
      city,
      state,
      zip
    })
  },

  // Registration Methods

  createAccount: async function (
    guid,
    password,
    emailOptIn,
    agreeToTerms,
    personalEmail,
    registrationCohort,
    preferredName
  ) {
    return this.apiV2JsonWithRedirect(`register/${guid}`, "POST", {
      guid,
      password,
      emailOptIn,
      agreeToTerms,
      personalEmail,
      registrationCohort,
      preferredName
    })
  },

  validatePassword: async function (password) {
    return this.apiV2JsonWithRedirect(`register/verify-password`, "POST", {
      password
    })
  },

  updatePassword: async function ({ currentPassword, newPassword, confirmNewPassword }) {
    return this.apiV2PostJson("auth/update-password", {
      currentPassword,
      newPassword,
      confirmNewPassword
    })
  },

  getRegisteringUser: async function (guid) {
    try {
      const headers = new Headers()
      headers.append("Content-Type", "application/json")
      const response = await fetchWrapper(`${this.baseUrl}/api/v2/register/${guid}`, {
        method: "GET",
        headers,
        credentials: "include"
      })
      const responseText = await response.text()
      const responseJson = JSON.parse(responseText)
      return Object.assign(new RegisteringUserModel(), responseJson)
    } catch (error) {
      if (error.name === HttpErrors.UNAUTHORIZED) {
        await Helpers.browserRedirect(`/?errorCode=${CarrotErrorCodes.EMPLOYEE_ALREADY_REGISTERED}`)
        return
      }

      // Catching issue with CORS and 404s as well, which both are not actionable
      if ([HttpErrors.NOT_FOUND, "FetchError"].includes(error.name)) {
        await Helpers.browserRedirect("/")
        return
      }
      reportError(error)
    }
  },

  // API V2 File Upload Request

  uploadReimbursementFile: async function (file, guid) {
    const response = await this.apiV2RequestWithRedirect(async () => {
      const body = new FormData()
      body.append("file", file)
      body.append("guid", guid)

      return fetchWrapper(this.baseUrl + "/api/v2/statements/uploadFile", {
        method: "POST",
        body,
        credentials: "include"
      })
    })

    const responseJson = await response.json()
    if (!responseJson.hasOwnProperty("fileGuid")) {
      throw new Error(`Response does not have field "fileGuid": ${JSON.stringify(responseJson)}`)
    }
    return responseJson.fileGuid
  },

  activateCard: function () {
    return this.apiV2JsonWithRedirect("card/activate", "POST", {})
  },

  getCardDetails: async function () {
    const response = await this.apiV2JsonWithRedirect("card/details", "GET")
    return await response.json()
  },

  getCurrentUser: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new UserInfoModel(), responseJson)
  },

  checkIfMerchantValid: async function (token) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/merchant/isValid/${token}`, {
      method: "GET",
      headers
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new MerchantValidModel(), responseJson)
  },

  approveMerchant: async function (token) {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/merchant/approve/${token}`, {
      method: "POST",
      headers
    })
  },

  getReimbursementDetails: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me/reimbursement-details`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return Object.assign(new ReimbursementDetailsModel(), responseJson)
  },

  verifyMemberInsurance: async function () {
    return this.apiV2PostJson("users/me/verify-member-insurance", null)
  },

  verifyPartnerInsurance: async function () {
    return this.apiV2PostJson("users/me/verify-partner-insurance", null)
  },

  updateUser: function (userInfoModel: UserInfoModel) {
    return this.apiV2JsonWithRedirect("users/me", "PATCH", userInfoModel)
  },

  forgotPassword: async function (email) {
    return this.apiV2PostJson("users/forgotPassword", { email })
  },

  getSupportCenterUrl: async function () {
    const response = await this.apiV2JsonWithRedirect("users/zendesk/support-center-url", "GET")
    const responseText = await response.text()
    const responseJson = JSON.parse(responseText)
    return responseJson.supportCenterUrl
  },

  getBenefitConfiguration: async function () {
    return await this.apiV2GetJson("users/me/benefit-configuration")
  },

  startRoutingInfoFlow: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info", "POST")
    return response
  },

  updateRoutingInfo: async function (payload) {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info", "PATCH", payload)
    return response
  },

  completeRoutingInfoFlow: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/routing-info/completed", "POST")
    return response
  },

  getCarrotPlansRoutingInfo: async function () {
    const response = await this.apiV2GetJson("users/me/routing-info")
    return response
  },

  startMedicalInfoFlow: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/medical-info", "POST")
    return response
  },

  updateMedicalInfo: async function (payload) {
    const response = await this.apiV2JsonWithRedirect("users/me/medical-info", "PATCH", payload)
    return response
  },

  getCarrotPlansMedicalInfo: async function () {
    const response = await this.apiV2GetJson("users/me/medical-info")
    return response
  },

  getAboutYou: async function () {
    const response = await this.apiV2GetJson("users/me/about-you")
    return response
  },

  enrollUserBenefitEnrollment: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/benefit-enrollment", "POST")
    return response
  },

  getBenefitEnrollmentPreview: async function () {
    const headers = new Headers()
    headers.append("Content-Type", "application/json")
    const response = await fetchWrapper(`${this.baseUrl}/api/v2/users/me/benefit-enrollment/preview`, {
      method: "GET",
      headers,
      credentials: "include"
    })
    const responseText = await response.text()
    return JSON.parse(responseText)
  },

  getBenefitEnrollmentStatus: async function (): Promise<BenefitEnrollmentStatus> {
    const { options, correlationId } = buildRequestOptions("GET")
    const response = await fetch(this._buildApiV2Url("users/me/enrollment-status"), options)
    return await processJsonResponse<BenefitEnrollmentStatus>(response, correlationId)
  },

  unPoseAsMember: async function (): Promise<Response> {
    const { options, correlationId } = buildRequestOptions("POST")
    return fetch(this._buildApiV2Url("member-pose/un-pose"), options)
  },

  // Carrot plans
  getCarrotPlan: async function (): Promise<CarrotPlan> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    const response = await fetch(`${this.baseUrl}/api/v2/users/me/carrot-plan`, {
      credentials: "include",
      headers,
      method: "GET"
    })

    return await processJsonResponse<CarrotPlan>(response, correlationId)
  },

  startCarrotPlansEnrollment: function () {
    return this.apiV2JsonWithRedirect("users/me/carrot-plans/routing/start", "POST")
  },

  completeCarrotPlansEnrollment: function () {
    return this.apiV2JsonWithRedirect("users/me/carrot-plans/routing/completed", "POST")
  },

  getCarrotPlanEmployeeSettings: async function (): Promise<CarrotPlanEmployeeSettings> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    const response = await fetch(`${this.baseUrl}/api/v2/users/me/carrot-plan/settings/employees`, {
      credentials: "include",
      headers,
      method: "GET"
    })
    return await processJsonResponse<CarrotPlanEmployeeSettings>(response, correlationId)
  },
  updateCarrotPlanStep: async function ({
    stepId,
    completed,
    markedNotRelevant
  }: CarrotPlanStepRequest): Promise<void> {
    const correlationId = uuid()
    const headers = new Headers()
    headers.append("X-Request-ID", correlationId)
    headers.append("Content-Type", "application/json")
    const body = JSON.stringify({
      stepId: stepId,
      completed: completed,
      markedNotRelevant: markedNotRelevant
    })
    await fetch(`${this.baseUrl}/api/v2/users/me/carrot-plan/steps/${stepId}`, {
      credentials: "include",
      body,
      headers,
      method: "POST"
    })
  },

  setInitialCarrotPlanChatRequested: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/carrot-plan/initial-carrot-plan-chat-requested", "POST")
    return response
  },

  setMemberScheduledInitialCarrotPlanExpertChat: async function () {
    const response = await this.apiV2JsonWithRedirect(
      "users/me/carrot-plan/initial-carrot-plan-expert-chat-requested",
      "POST"
    )
    return response
  },

  setAcknowledgedCarrotPlansPopup: async function () {
    const response = await this.apiV2JsonWithRedirect("users/me/carrot-plan/popup", "POST")
    return response
  },

  _buildApiV2Url: function buildApiV2Url(path: string): string {
    return `${this.baseUrl}/api/v2/${path}`
  },

  triggerQleEnrollment: async function (): Promise<boolean> {
    const { options } = buildRequestOptions("POST")
    const response = await fetch(this._buildApiV2Url("enrollments/qle"), options)
    return response.ok
  },

  triggerInsuranceEdit: async function (): Promise<boolean> {
    const { options } = buildRequestOptions("POST")
    const response = await fetch(this._buildApiV2Url("enrollments/clear/insurance"), options)
    return response.ok
  },

  carrotPlanJourneyCompleted: async function (): Promise<boolean> {
    const response = await this.apiV2JsonWithRedirect("users/me/carrot-plan/journey-complete", "POST")
    return response.ok
  },

  carrotPlanJourneyCompleteMemberNote: async function (memberNote: string): Promise<boolean> {
    const response = await this.apiV2JsonWithRedirect("users/me/carrot-plan/journey-complete-member-note", "POST", {
      memberNote
    })
    return response.ok
  }
}

export default CarrotClient
